import React, { useState, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Card, Badge, Offcanvas, OffcanvasHeader, OffcanvasBody, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { useColorPalette } from '../hooks/useColorPalette';
import AppointmentCard from './AppointmentCard';
import { getNodeApiUrl, getApiBaseUrl } from '../config/api.config';

/**
 * Componente per la visualizzazione di una singola card di richiesta di assistenza
 */
const AssistanceRequestCard = ({ 
  request, 
  getCardBackgroundColor, 
  getStatusColor,
  onClick,
  onStatusChange,
  onEditRequest,
  onDeleteRequest,
  onOpenScheduling 
}) => {
  // Utilizzo dell'hook useColorPalette per ottenere i colori dinamici
  const colors = useColorPalette();
  
  // Stati per la gestione delle note e dell'offcanvas
  const [expandedNotes, setExpandedNotes] = useState(false);
  const [showDetailsOffcanvas, setShowDetailsOffcanvas] = useState(false);
  
  // Stati per la gestione degli appuntamenti
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState(null);
  
  // Stati per la gestione della commessa associata
  const [associatedJob, setAssociatedJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobError, setJobError] = useState(null);
  
  // Stato per la modale di avviso "nessuna commessa"
  const [showNoJobModal, setShowNoJobModal] = useState(false);
  
  // Effetto per caricare gli appuntamenti e la commessa quando l'offcanvas è aperto
  useEffect(() => {
    if (showDetailsOffcanvas && request._id) {
      fetchAppointments();
      fetchAssociatedJob();
    }
  }, [showDetailsOffcanvas, request._id]);
  
  // Funzione per recuperare gli appuntamenti associati alla richiesta
  const fetchAppointments = async () => {
    if (!request._id) return;
    
    setLoadingAppointments(true);
    setAppointmentsError(null);
    
    try {
      // Recupero il token dall'localStorage
      const token = localStorage.getItem('installamelo-auth-token');
      
      const response = await axios.get(getNodeApiUrl('appointments/byAssistanceRequest'), {
        params: {
          assistanceRequestId: request._id,
          accountId: request.accountId
        },
        headers: {
          'auth-token': token
        }
      });
      
      if (response.data && response.data.data) {
        setAppointments(response.data.data);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento degli appuntamenti:', error);
      setAppointmentsError('Impossibile caricare gli appuntamenti');
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  // Funzione per recuperare la commessa associata alla richiesta
  const fetchAssociatedJob = async () => {
    if (!request._id) return;
    
    setLoadingJob(true);
    setJobError(null);
    
    try {
      // Recupero il token dall'localStorage
      const token = localStorage.getItem('installamelo-auth-token');
      
      const response = await axios.get(getNodeApiUrl('jobs/byAssistanceRequest'), {
        params: {
          assistanceRequestId: request._id,
          accountId: request.accountId
        },
        headers: {
          'auth-token': token
        }
      });
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setAssociatedJob(response.data.data[0]); // Prende la prima commessa trovata
      } else {
        setAssociatedJob(null);
      }
    } catch (error) {
      console.error('Errore nel caricamento della commessa associata:', error);
      setJobError('Impossibile caricare la commessa associata');
      setAssociatedJob(null);
    } finally {
      setLoadingJob(false);
    }
  };
  
  // Funzione per troncare il testo alle prime X parole
  const truncateText = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };
  
  // Handler per il toggle dell'espansione delle note
  const toggleNotesExpansion = (e) => {
    e.stopPropagation(); // Impedisce che il click attivi anche il click sulla card
    setExpandedNotes(!expandedNotes);
  };
  
  // Handler per il click sull'icona calendario - apre SchedulingOffcanvas con commessa preselezionata
  const handleCalendarClick = async (e) => {
    e.stopPropagation();
    
    // Prima verifichiamo se c'è una commessa associata
    if (!request._id) {
      toast.error('Richiesta non valida');
      return;
    }
    
    try {
      const token = localStorage.getItem('installamelo-auth-token');
      
      // Cerca la commessa associata a questa richiesta di assistenza
      const response = await axios.get(getNodeApiUrl('jobs/byAssistanceRequest'), {
        params: {
          assistanceRequestId: request._id,
          accountId: request.accountId
        },
        headers: {
          'auth-token': token
        }
      });
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        const job = response.data.data[0];
        
        // Formatta la commessa nel formato atteso da JobSelect/SchedulingOffcanvas
        const formattedJob = {
          value: job._id,
          label: `${job.code || `${job.progr}/${job.year}`} - ${job.customerLabel || 'N/D'} (${job.jobTypeLabel || 'N/D'})`,
          data: job,
          systemLabel: job.systemLabel,
          systemAddressLabel: job.systemAddressLabel
        };
        
        // Usa la callback per aprire lo SchedulingOffcanvas a livello di pagina
        if (onOpenScheduling) {
          onOpenScheduling(formattedJob);
        }
      } else {
        console.log('Nessuna commessa trovata per questa assistenza');
        setShowNoJobModal(true);
      }
    } catch (error) {
      console.error('Errore nel verificare la commessa associata:', error);
      toast.error('Errore nel verificare la commessa associata');
    }
  };
  
  return (
    <>
      <div
        className="assistance-request-card"
        style={{ 
          cursor: 'default', 
          marginBottom: '15px', 
          marginRight: '5px', 
          border: '1px solid #ddd', 
          borderRadius: '4px', 
          backgroundColor: 'white', 
          width: '100%', 
          maxWidth: '420px', 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #ddd',
          backgroundColor: getCardBackgroundColor(request),
          color: '#333333'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: '0', fontWeight: 'bold', display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
              <span 
                title={request.customerLabel || 'Cliente N/D'}
                style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: '210px', // Ridotta rispetto all'indirizzo (260px) di circa 5 caratteri
                  display: 'inline-block'
                }}
              >
                {request.customerLabel || 'Cliente N/D'}
              </span>
              {request.createdByCustomer === true && (
                <i 
                  className="fa-solid fa-mobile-screen" 
                  style={{ 
                    marginLeft: '8px', 
                    color: '#9b59b6',
                    fontSize: '0.9rem'
                  }}
                  title="Creata dal cliente via App"
                ></i>
              )}
              {request.teleassistenza === true && (
                <i 
                  className="fa-solid fa-headset" 
                  style={{ 
                    marginLeft: '8px', 
                    color: '#4299e1',
                    fontSize: '0.9rem'
                  }}
                  title="Richiesta di Teleassistenza"
                ></i>
              )}
            </h3>
            <span>{request.code || 'Codice N/D'}</span>
          </div>
          
          {/* Informazioni di contatto */}
          <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', minHeight: '20px' }}>
              <i className="fa-solid fa-location-dot" style={{ opacity: 0.7 }}></i>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }} title={request.systemAddressLabel || 'Indirizzo non specificato'}>
                {request.systemAddressLabel || <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Indirizzo non specificato</span>}
              </span>
            </div>
          </div>
        </div>

        {/* Stato - attaccato all'header */}
        <div style={{ 
          textAlign: 'center', 
          padding: '6px', 
          width: '100%',
          backgroundColor: getStatusColor(request),
          color: '#ffffff',
          fontWeight: 'bold'
        }}>
          {request.statusLabel || 'STATO N/D'}
        </div>
        
        {/* Corpo */}
        <div style={{ padding: '16px', flex: 1 }}>
          
          {/* Tipo di sistema e impianto */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <i className="fa-solid fa-plug" style={{ opacity: 0.7 }}></i>
            <span>
              {request.systemTypeLabel || 'Tipo sistema N/D'}
              {request.systemLabel && ` - ${request.systemLabel}`}
            </span>
          </div>

          {/* Tipo assistenza + descrizione */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <i className="fa-solid fa-wrench" style={{ opacity: 0.7 }}></i>
            <span>
              {request.assistanceTypeLabel || 'Tipo assistenza N/D'}
              {request.assistanceTypeDescription && ` - ${request.assistanceTypeDescription}`}
              {request.teleassistenza === true && (
                <span 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginLeft: '8px',
                    backgroundColor: '#4299e1',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <i className="fa-solid fa-video" style={{ marginRight: '4px' }}></i>
                  Teleassistenza
                </span>
              )}
            </span>
          </div>
          
          {/* Data scadenza */}
          {request.expirationDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <i className="fa-solid fa-clock" style={{ opacity: 0.7 }}></i>
              <span>Scadenza: {moment(request.expirationDate).format('DD/MM/YYYY')}</span>
            </div>
          )}

          {/* Fascia oraria appuntamento - con fallback per fasciaOraria */}
          {(request.appointmentTimeSlot || request.fasciaOraria) && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              marginBottom: '8px',
              backgroundColor: `${colors.primary}15` || '#3a5a4015',
              padding: '6px 10px',
              borderRadius: '6px'
            }}>
              <i className="fa-regular fa-clock" style={{ color: colors.primary || '#3a5a40' }}></i>
              <span style={{ fontWeight: '500' }}>Fascia oraria: <strong>{request.appointmentTimeSlot || request.fasciaOraria}</strong></span>
            </div>
          )}
        </div>

        {/* Note con espansione */}
        {request.customerRequest && (
          <div style={{ margin: '12px 0', padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px' }}>
            <div style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Note:</span>
              <i 
                className={expandedNotes ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"} 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: '0.9rem',
                  color: colors.primary || '#3a5a40',
                  padding: '4px',
                  borderRadius: '50%',
                  backgroundColor: `${colors.primary}15` || '#3a5a4015'
                }}
                title={expandedNotes ? "Comprimi" : "Espandi"}
                onClick={toggleNotesExpansion}
              ></i>
            </div>
            <div style={{ fontSize: '0.9rem', marginTop: '4px' }}>
              {expandedNotes ? request.customerRequest : truncateText(request.customerRequest, 20)}
              {!expandedNotes && request.customerRequest.split(' ').length > 20 && (
                <span 
                  onClick={toggleNotesExpansion}
                  style={{ 
                    marginLeft: '5px', 
                    color: colors.primary || '#3a5a40', 
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Mostra tutto
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Files */}
        {request.uploads && request.uploads.length > 0 && (
          <div style={{ margin: '12px 0', padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px' }}>
            <div style={{ fontWeight: 'bold' }}>Files:</div>
            <div style={{ marginTop: '4px' }}>{request.uploads.length} file</div>
          </div>
        )}
        
        {/* Casi d'uso */}
        {request.useCases && request.useCases.length > 0 && (
          <div style={{ margin: '12px 0', padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px' }}>
            <div style={{ fontWeight: 'bold' }}>Richieste:</div>
            <ul style={{ paddingLeft: '20px', margin: '4px 0' }}>
              {request.useCases.map((useCase, idx) => (
                <li key={idx}>{typeof useCase === 'object' ? (
                  useCase.title ? 
                  useCase.title : 
                  useCase.useCase || useCase.useCaseLabel || useCase
                ) : useCase}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Separatore */}
        <div style={{ borderTop: '1px solid #ddd', margin: '12px 0' }}></div>

        {/* Dettagli amministrativi */}
        <div style={{ margin: '12px 0', padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px', fontSize: '0.9rem' }}>
          <div>Manutenzione: {(request.manteinanceContractTop || !!request.manteinanceContractId) ? 'SI' : 'NO'}</div>
          <div>Garanzia: {request.warranty ? 'SI' : 'NO'}</div>
          <div>Pagamento: {request.paymentTypeLabel || 'N/D'}</div>
          <div>Data Creazione: {request.creationDate ? new Date(request.creationDate).toLocaleDateString() : 'N/D'}</div>
          {request.assistanceTypePriorityValue && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>Priorità: </span>
              <span 
                style={{ 
                  marginLeft: '6px', 
                  padding: '2px 8px',
                  borderRadius: '12px',
                  backgroundColor: request.assistanceTypeColor || colors.primary || '#3a5a40',
                  color: 'white',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {request.assistanceTypePriorityLabel || 'Standard'}
              </span>
            </div>
          )}
        </div>

        {/* Dettagli di chiusura */}
        {request.closedDate && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #ddd' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>DETTAGLI CHIUSURA</div>
            <div style={{ fontSize: '0.9rem' }}>
              <div>Data: {new Date(request.closedDate).toLocaleDateString()}</div>
            </div>
          </div>
        )}

        {/* Pulsanti azioni */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'space-around' }}>
          <button 
            style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (onStatusChange) onStatusChange(request);
            }}
            title="Cambia stato"
          >
            <i className="fa-solid fa-rotate"></i>
          </button>
          <button 
            style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (onEditRequest) onEditRequest(request);
            }}
            title="Modifica richiesta"
          >
            <i className="fa-solid fa-pen"></i>
          </button>
          <button 
            style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7 }}
            onClick={handleCalendarClick}
            title="Pianifica appuntamento"
          >
            <i className="fa-solid fa-calendar-days"></i>
          </button>
          <button 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              opacity: 0.7, 
              color: colors.primary || '#3a5a40' 
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowDetailsOffcanvas(true);
            }}
            title="Visualizza dettagli"
          >
            <i className="fa-solid fa-eye"></i>
          </button>
          <button 
            style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7, color: '#dc3545' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onDeleteRequest) onDeleteRequest(request);
            }}
            title="Elimina richiesta"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>

      {/* Offcanvas per visualizzare i dettagli dell'assistenza */}
      <Offcanvas 
        isOpen={showDetailsOffcanvas} 
        toggle={() => setShowDetailsOffcanvas(!showDetailsOffcanvas)} 
        direction="end"
        style={{ width: '40%', maxWidth: '90vw', minWidth: '350px' }}
        unmountOnClose={true}
        returnFocusAfterClose={false}
      >
        <OffcanvasHeader 
          toggle={() => setShowDetailsOffcanvas(false)}
          style={{ 
            backgroundColor: getCardBackgroundColor(request),
            borderBottom: '1px solid rgba(0,0,0,0.1)' 
          }}
        >
          <div>
            <h5 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {request.customerLabel || 'Cliente N/D'}
              {request.teleassistenza === true && (
                <i 
                  className="fa-solid fa-headset" 
                  style={{ 
                    marginLeft: '8px', 
                    color: colors.secondary || '#4299e1',
                    fontSize: '0.9rem'
                  }}
                  title="Teleassistenza"
                ></i>
              )}
            </h5>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              {request.code || `${request.progr || ''}/${request.year || ''}`}
            </div>
          </div>
        </OffcanvasHeader>
        <OffcanvasBody>
          {/* Richiesta Cliente e Data Scadenza - Evidenziati */}
          <div style={{ 
            marginBottom: '1.5rem', 
            padding: '1rem',
            backgroundColor: `${colors.primary}15` || '#f0f7f0',
            borderRadius: '8px',
            border: `1px solid ${colors.primary}30` || '#d8e6d8'
          }}>
            {request.customerRequest && (
              <div style={{ marginBottom: '1rem' }}>
                <h6 style={{ 
                  fontWeight: 'bold', 
                  color: colors.primary || '#3a5a40',
                  borderBottom: `1px solid ${colors.primary}30` || '#d8e6d8',
                  paddingBottom: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <i className="fa-solid fa-comment-dots me-2"></i>
                  Richiesta Cliente
                </h6>
                <div style={{ 
                  fontSize: '1rem',
                  whiteSpace: 'pre-wrap',
                  fontWeight: '500'
                }}>
                  {request.customerRequest}
                </div>
              </div>
            )}
            
            {request.expirationDate && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: (request.appointmentTimeSlot || request.fasciaOraria) ? '0.75rem' : 0
              }}>
                <div style={{ 
                  fontWeight: 'bold',
                  color: colors.primary || '#3a5a40'
                }}>
                  <i className="fa-solid fa-calendar-exclamation me-2"></i>
                  Data Scadenza:
                </div>
                <div style={{ 
                  backgroundColor: moment(request.expirationDate).isBefore(new Date()) ? '#f8d7da' : '#d4edda',
                  color: moment(request.expirationDate).isBefore(new Date()) ? '#721c24' : '#155724',
                  padding: '0.3rem 0.7rem',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}>
                  {new Date(request.expirationDate).toLocaleDateString()}
                </div>
              </div>
            )}
            
            {/* Fascia oraria preferita dal cliente - con fallback per fasciaOraria */}
            {(request.appointmentTimeSlot || request.fasciaOraria) && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ 
                  fontWeight: 'bold',
                  color: colors.primary || '#3a5a40'
                }}>
                  <i className="fa-regular fa-clock me-2"></i>
                  Fascia Oraria:
                </div>
                <div style={{ 
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  padding: '0.3rem 0.7rem',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}>
                  {request.appointmentTimeSlot || request.fasciaOraria}
                </div>
              </div>
            )}
          </div>
          
          {/* Informazioni generali */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h6 style={{ 
              fontWeight: 'bold', 
              borderBottom: '1px solid rgba(0,0,0,0.1)', 
              paddingBottom: '0.5rem',
              color: colors.primary || '#3a5a40'
            }}>
              <i className="fa-solid fa-circle-info me-2"></i>
              Informazioni Generali
            </h6>
            
            <div style={{ fontSize: '0.9rem', paddingLeft: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>Tipo:</strong>
                <span>{request.assistanceTypeLabel || 'N/D'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>Priorità:</strong>
                <span 
                  style={{ 
                    padding: '1px 8px',
                    borderRadius: '12px',
                    backgroundColor: request.assistanceTypeColor || colors.primary || '#3a5a40',
                    color: 'white',
                    fontSize: '0.8rem',
                  }}
                >
                  {request.assistanceTypePriorityLabel || 'Standard'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>Data creazione:</strong>
                <span>{request.creationDate ? new Date(request.creationDate).toLocaleDateString() : 'N/D'}</span>
              </div>
              {request.createdByCustomer === true && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Origine:</strong>
                  <span style={{
                    backgroundColor: '#9b59b6',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <i className="fa-solid fa-mobile-screen"></i>
                    Creata dal cliente
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>Indirizzo:</strong>
                <span style={{ maxWidth: '230px', textAlign: 'right' }}>
                  {request.systemAddressLabel || 'Indirizzo non specificato'}
                </span>
              </div>
              
              {/* Tipo di impianto */}
              {request.systemTypeLabel && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Tipo impianto:</strong>
                  <span style={{ maxWidth: '230px', textAlign: 'right' }}>
                    {request.systemTypeLabel}
                  </span>
                </div>
              )}
                
                {/* Informazioni sulla distanza dall'impianto */}
                {(request.geoData?.distanceKm || request.geoData?.distanceTime) && (
                  <div style={{ 
                    marginBottom: '0.8rem', 
                    backgroundColor: `${colors.primary}10` || '#3a5a4010',
                    borderRadius: '4px', 
                    padding: '0.5rem'
                  }}>
                    <div style={{ 
                      fontSize: '0.85rem', 
                      fontWeight: 'bold', 
                      marginBottom: '0.3rem',
                      color: colors.primary || '#3a5a40'
                    }}>
                      <i className="fa-solid fa-map-marker-alt" style={{ marginRight: '5px' }}></i>
                      Distanza dall'impianto
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem' }}>
                      {request.geoData?.distanceKm && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className="fa-solid fa-road" style={{ color: colors.primary || '#3a5a40', marginRight: '5px' }}></i>
                          <span>
                            {typeof request.geoData.distanceKm === 'number' ? `${request.geoData.distanceKm.toFixed(1)} km` : request.geoData.distanceKm}
                          </span>
                        </div>
                      )}
                      {request.geoData?.distanceTime && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className="fa-solid fa-clock" style={{ color: colors.primary || '#3a5a40', marginRight: '5px' }}></i>
                          <span>
                            {typeof request.geoData.distanceTime === 'number' ? `${request.geoData.distanceTime} min` : request.geoData.distanceTime}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Garanzia:</strong>
                  <span>{request.warranty ? 'SI' : 'NO'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Manutenzione:</strong>
                  <span>{(request.manteinanceContractTop || !!request.manteinanceContractId) ? 'SI' : 'NO'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Pagamento:</strong>
                  <span>{request.paymentTypeLabel || 'N/D'}</span>
                </div>
                {request.closedDate && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <strong>Data chiusura:</strong>
                    <span>{new Date(request.closedDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Commessa Associata */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h6 style={{ 
                fontWeight: 'bold', 
                borderBottom: '1px solid rgba(0,0,0,0.1)', 
                paddingBottom: '0.5rem',
                color: colors.primary || '#3a5a40',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <i className="fa-solid fa-briefcase me-2"></i>
                  Commessa Associata
                </div>
                {loadingJob && (
                  <Spinner size="sm" color="primary" />
                )}
              </h6>
              
              {jobError && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>
                  {jobError}
                </div>
              )}
              
              {!loadingJob && !jobError && !associatedJob && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <i className="fa-solid fa-briefcase-blank me-2"></i>
                  Nessuna commessa associata
                </div>
              )}
              
              {!loadingJob && associatedJob && (
                <div style={{ 
                  marginTop: '0.5rem',
                  padding: '1rem',
                  backgroundColor: `${colors.primary}10` || '#f0f7f0',
                  borderRadius: '8px',
                  border: `1px solid ${colors.primary}30` || '#d8e6d8'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: colors.primary || '#3a5a40'
                    }}>
                      {associatedJob.code || `${associatedJob.progr}/${associatedJob.year}`}
                    </div>
                    <div style={{ 
                      backgroundColor: colors.primary || '#3a5a40',
                      color: 'white',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {associatedJob.statusLabel || 'N/D'}
                    </div>
                  </div>
                  
                  {associatedJob.description && (
                    <div style={{ 
                      fontSize: '0.9rem',
                      marginBottom: '0.5rem',
                      fontStyle: 'italic'
                    }}>
                      {associatedJob.description}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    <div>Tipo: {associatedJob.jobTypeLabel || 'N/D'}</div>
                    {associatedJob.creationDate && (
                      <div>Creata: {new Date(associatedJob.creationDate).toLocaleDateString()}</div>
                    )}
                    {associatedJob.scheduledDate && (
                      <div>Programmata: {new Date(associatedJob.scheduledDate).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Note */}
            {request.notes && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h6 style={{ 
                  fontWeight: 'bold', 
                  borderBottom: '1px solid rgba(0,0,0,0.1)', 
                  paddingBottom: '0.5rem',
                  color: colors.primary || '#3a5a40'
                }}>
                  <i className="fa-solid fa-note-sticky me-2"></i>
                  Note
                </h6>
                <div style={{ 
                  fontSize: '0.9rem', 
                  padding: '0.5rem',
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap' 
                }}>
                  {request.notes}
                </div>
              </div>
            )}

            {/* Sezione Appuntamenti */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h6 style={{ 
                fontWeight: 'bold', 
                borderBottom: '1px solid rgba(0,0,0,0.1)', 
                paddingBottom: '0.5rem',
                color: colors.primary || '#3a5a40',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <i className="fa-solid fa-calendar-days me-2"></i>
                  Appuntamenti
                </div>
                {loadingAppointments && (
                  <Spinner size="sm" color="primary" />
                )}
              </h6>
              
              {appointmentsError && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>
                  {appointmentsError}
                </div>
              )}
              
              {!loadingAppointments && !appointmentsError && appointments.length === 0 && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <i className="fa-solid fa-calendar-xmark me-2"></i>
                  Nessun appuntamento programmato
                </div>
              )}
              
              {!loadingAppointments && appointments.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  {appointments.map((appointment, idx) => (
                    <div key={idx} style={{ marginBottom: idx < appointments.length - 1 ? '1rem' : 0 }}>
                      <AppointmentCard 
                        appointment={appointment} 
                        apiBaseUrl={getApiBaseUrl()} 
                        token={localStorage.getItem('token')}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          
          {/* File allegati */}
          {request.uploads && request.uploads.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h6 style={{ 
                fontWeight: 'bold', 
                borderBottom: '1px solid rgba(0,0,0,0.1)', 
                paddingBottom: '0.5rem',
                color: colors.primary || '#3a5a40'
              }}>
                <i className="fa-solid fa-paperclip me-2"></i>
                Allegati ({request.uploads.length})
              </h6>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.5rem',
                marginTop: '0.5rem' 
              }}>
                {request.uploads.map((file, idx) => {
                  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
                    (file.fileName || file.name || '').split('.').pop().toLowerCase()
                  );
                  
                  const getFileIcon = (file) => {
                    const ext = (file.fileName || file.name || '').split('.').pop().toLowerCase();
                    const iconMap = {
                      'pdf': 'fa-file-pdf',
                      'doc': 'fa-file-word', 'docx': 'fa-file-word',
                      'xls': 'fa-file-excel', 'xlsx': 'fa-file-excel',
                      'jpg': 'fa-file-image', 'jpeg': 'fa-file-image', 'png': 'fa-file-image', 
                      'gif': 'fa-file-image', 'svg': 'fa-file-image', 'webp': 'fa-file-image'
                    };
                    return `fa-solid ${iconMap[ext] || 'fa-file'}`;
                  };
                  
                  const getFileUrl = (file) => {
                    if (file.urlFile) return file.urlFile;
                    if (file.url) return file.url;
                    
                    const fileName = file.fileName || file.name;
                    return `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${fileName}`;
                  };
                  
                  return (
                    <div 
                      key={idx} 
                      style={{ 
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '120px',
                        cursor: 'pointer'
                      }}
                      title={file.fileName || file.name || 'File'}
                      onClick={() => window.open(getFileUrl(file), '_blank')}
                    >
                      {isImage ? (
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <img 
                            src={getFileUrl(file)} 
                            alt={file.fileName || file.name || 'Anteprima'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '0.5rem'
                        }}>
                          <i 
                            className={getFileIcon(file)} 
                            style={{ fontSize: '2rem', marginBottom: '0.5rem', color: colors.primary || '#3a5a40' }}
                          ></i>
                        </div>
                      )}
                      <div style={{ 
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'rgba(0,0,0,0.03)',
                        borderTop: '1px solid #ddd',
                        fontSize: '0.75rem',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {file.fileName || file.name || 'File'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
      
      {/* Modale professionale per avviso "nessuna commessa associata" */}
      <Modal 
        isOpen={showNoJobModal} 
        toggle={() => setShowNoJobModal(false)}
        centered
        style={{ zIndex: 200010 }}
      >
        <ModalHeader 
          toggle={() => setShowNoJobModal(false)}
          style={{ 
            backgroundColor: '#fff3cd',
            borderBottom: '1px solid #ffc107',
            color: '#856404'
          }}
        >
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          Attenzione
        </ModalHeader>
        <ModalBody style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#fff3cd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <i className="fa-solid fa-briefcase" style={{ fontSize: '36px', color: '#856404' }}></i>
            </div>
            <h5 style={{ fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>
              Nessuna commessa associata
            </h5>
            <p style={{ color: '#666', marginBottom: '0', lineHeight: '1.6' }}>
              Per pianificare un appuntamento è necessario prima creare una commessa associata a questa richiesta di assistenza.
            </p>
          </div>
        </ModalBody>
        <ModalFooter style={{ justifyContent: 'center', borderTop: '1px solid #eee', padding: '16px' }}>
          <Button 
            color="warning" 
            onClick={() => setShowNoJobModal(false)}
            style={{ 
              minWidth: '120px',
              fontWeight: '600'
            }}
          >
            Ho capito
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

AssistanceRequestCard.propTypes = {
  request: PropTypes.object.isRequired,
  getCardBackgroundColor: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onStatusChange: PropTypes.func,
  onEditRequest: PropTypes.func,
  onDeleteRequest: PropTypes.func
};

export default AssistanceRequestCard;
